import { BrandRailLink } from '@/components/BrandRailLink';
import { UserMenu } from '@/components/UserMenu';
import { Badge } from '@/components/ui/badge';
import {
  SidebarContent as SidebarContentArea,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useMe } from '@/hooks/use-me';
import { AppShell } from '@/layouts/AppShell';
import {
  Building2,
  Cpu,
  History,
  KeyRound,
  LogIn,
  type LucideIcon,
  Mail,
  Network,
  Package,
  ShieldCheck,
  UsersRound,
  Workflow,
} from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

interface AdminNavItem {
  key: string;
  icon: LucideIcon;
  label: string;
  visible: boolean;
}

/**
 * One admin nav row. Same primitive as the console rail, so hovering, the
 * active tint, the collapsed-rail tooltip and the `aria-current` contract are
 * identical on both sides of the product.
 */
function AdminRow({ item, active }: { item: AdminNavItem; active: boolean }) {
  const Icon = item.icon;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
        <Link to={item.key} aria-current={active ? 'page' : undefined}>
          <Icon aria-hidden />
          <span>{item.label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/**
 * The admin rail.
 *
 * Admin is the same product as the console — same brand block (which is what
 * takes you back), same nav rows, same account footer — with a completely
 * different set of destinations. Building it on the console's primitives is
 * what makes that read as one app rather than two.
 */
export function AdminSidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const { data: me } = useMe();
  const { t } = useTranslation();

  const isSuperAdmin = !!me?.user.is_super_admin;
  const isCompanyAdmin = !!me?.user.company_admin;
  const isAdminish = isSuperAdmin || isCompanyAdmin;

  const items: AdminNavItem[] = useMemo(
    () => [
      {
        key: '/admin/users',
        icon: ShieldCheck,
        label: t('admin.users_title', 'Users'),
        visible: isAdminish,
      },
      {
        key: '/admin/member-groups',
        icon: UsersRound,
        label: t('admin.member_groups_title', 'User groups'),
        visible: isAdminish,
      },
      {
        key: '/admin/companies',
        icon: Building2,
        label: t('admin.companies_title', 'Companies'),
        visible: isSuperAdmin,
      },
      {
        key: '/admin/workers',
        icon: Cpu,
        label: t('admin.workers_title', 'Workers'),
        visible: isSuperAdmin,
      },
      {
        key: '/admin/relays',
        icon: Network,
        label: t('admin.relays_title', 'Relays'),
        visible: isSuperAdmin,
      },
      {
        key: '/admin/pi-credentials',
        icon: KeyRound,
        label: t('admin.pi_creds_title', 'PI Credentials'),
        visible: isSuperAdmin,
      },
      {
        key: '/admin/mail',
        icon: Mail,
        label: t('admin.mail_title', 'Mail notifications'),
        visible: isSuperAdmin,
      },
      {
        key: '/admin/audit-logs',
        icon: History,
        label: t('admin.audit_logs_title', 'Audit Logs'),
        visible: isAdminish,
      },
      {
        key: '/admin/queue-health',
        icon: Workflow,
        label: t('admin.queue_health_title', 'Queue Health'),
        visible: isSuperAdmin,
      },
      {
        key: '/admin/oidc-providers',
        icon: LogIn,
        label: t('admin.oidc_title', 'OIDC Providers'),
        visible: isSuperAdmin,
      },
      {
        key: '/admin/releases',
        icon: Package,
        label: t('admin.releases_title', 'Releases'),
        visible: isSuperAdmin,
      },
    ],
    [isAdminish, isSuperAdmin, t],
  );

  const visible = items.filter((n) => n.visible);
  const selectedKey =
    visible
      .map((n) => n.key)
      .filter((p) => location.pathname.startsWith(p))
      .sort((a, b) => b.length - a.length)[0] ?? visible[0]?.key;

  return (
    <>
      <SidebarHeader className="gap-2">
        <BrandRailLink onNavigate={onNavigate} />
      </SidebarHeader>

      <SidebarContentArea>
        <SidebarGroup>
          <SidebarGroupLabel>{t('admin.admin_section', 'Admin')}</SidebarGroupLabel>
          <SidebarMenu>
            {visible.map((item) => (
              <AdminRow key={item.key} item={item} active={selectedKey === item.key} />
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContentArea>

      <SidebarFooter>
        <UserMenu />
      </SidebarFooter>
    </>
  );
}

/**
 * `/admin` shell. The header carries only the mode badge: the rail already
 * shows where you are, so a second set of admin tabs would be noise.
 */
export function AdminLayout() {
  const { t } = useTranslation();

  return (
    <AppShell
      sidebar={<AdminSidebarContent />}
      header={
        <Badge
          variant="destructive"
          className="h-6 gap-1.5 px-2.5 text-xs font-semibold"
          aria-label={t('admin.admin_mode', 'Admin mode')}
        >
          <ShieldCheck aria-hidden />
          {t('admin.admin_mode', 'Admin mode')}
        </Badge>
      }
    />
  );
}
